import { useSuspenseQuery } from "@apollo/client";
import {
  CurrencyAmountUI,
  cn,
  DataTable,
  DataTableColumnHeader,
  PrimaryFullScreenSpinner,
} from "@repo/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { differenceInDays } from "date-fns";
import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { Suspense } from "react";
import { RiDownload2Fill } from "react-icons/ri";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/unit-prices")({
  head: () => opsHead("Unit Prices"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const adminUnitPricesDocument = graphql(`
  query AdminUnitPrices {
    products {
      id
      internalName
      provider {
        id
        name
      }
      ... on FundProduct {
        unit {
          id
          unitPriceFileUrl
        }
        latestUnitPrice {
          at
          price
          targetUnit
        }
      }
    }
  }
`);

type UnitPriceListItem = {
  id: string;
  productId: string;
  internalName: string;
  providerName: string;
  unitPriceFileUrl: string | null;
  unitPrice?: {
    at: Date;
    price: Decimal;
    currency: string;
  } | null;
};

function View() {
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(adminUnitPricesDocument);

  const unitPriceListItems: UnitPriceListItem[] = (data.products ?? []).flatMap(
    (product) => {
      if (product.__typename !== "FundProduct" || !product.unit) {
        return [];
      }

      return [
        {
          id: product.unit.id,
          productId: product.id,
          internalName: product.internalName,
          providerName: product.provider?.name ?? "-",
          unitPriceFileUrl: product.unit.unitPriceFileUrl ?? null,
          unitPrice: product.latestUnitPrice
            ? {
                at: product.latestUnitPrice.at,
                price: new Decimal(product.latestUnitPrice.price),
                currency: product.latestUnitPrice.targetUnit,
              }
            : null,
        },
      ];
    },
  );

  return (
    <AdminPageContainer testId="unit-price-list">
      <AdminPageHeader
        title="Unit Prices"
        subtitle="These are the unit prices of fund products in Primary's codebase."
      />
      <DataTable
        data={unitPriceListItems}
        columnSortProps={{
          id: "internal-name",
          desc: false,
        }}
        columns={columns}
        onRowClick={(row) =>
          navigate({ to: `/products/${row.original.productId}/unit-prices` })
        }
        initialState={{
          pagination: {
            pageSize: 50,
          },
        }}
        className="overflow-hidden border"
      />
    </AdminPageContainer>
  );
}

const columns: ColumnDef<UnitPriceListItem>[] = [
  {
    id: "internal-name",
    accessorKey: "internalName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Internal name" />
    ),
  },
  {
    id: "provider",
    accessorKey: "providerName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Provider" />
    ),
  },
  {
    id: "unit-price-value",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Unit Price" />
    ),
    cell: ({
      row: {
        original: { unitPrice },
      },
    }) => {
      if (!unitPrice) {
        return "-";
      }

      return (
        <CurrencyAmountUI
          amount={unitPrice.price}
          currency={unitPrice.currency}
        />
      );
    },
  },
  {
    id: "unit-price-as-of",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="As of" />
    ),
    cell: ({
      row: {
        original: { unitPrice },
      },
    }) => {
      if (!unitPrice) {
        return "-";
      }

      const utcDateTime = DateTime.fromJSDate(unitPrice.at).setZone("UTC");

      return (
        <div className="flex-1 space-x-1 text-sm">
          <div className="inline-flex items-baseline text-sm">
            {utcDateTime.toFormat("yyyy-MM-dd")}
          </div>
          <div className="inline-flex items-baseline text-sm">
            {utcDateTime.toFormat("HH:mm")}
          </div>
          <div className="inline-flex items-baseline text-xs">
            {utcDateTime.toFormat("ZZZZ")}
          </div>
        </div>
      );
    },
  },
  {
    id: "unit-price-last-updated-at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Last Updated At" />
    ),
    cell: ({
      row: {
        original: { unitPrice },
      },
    }) => {
      if (!unitPrice) {
        return "-";
      }

      const utcDateTime = DateTime.fromJSDate(unitPrice.at).setZone("UTC");
      const showWarnStyling = differenceInDays(new Date(), unitPrice.at) >= 7;

      return (
        <div
          className={cn(
            "flex-1 space-x-1 text-sm",
            showWarnStyling && "font-semibold text-warning",
          )}
        >
          <div className="inline-flex items-baseline text-sm">
            {utcDateTime.toFormat("yyyy-MM-dd")}
          </div>
          <div className="inline-flex items-baseline text-sm">
            {utcDateTime.toFormat("HH:mm")}
          </div>
          <div className="inline-flex items-baseline text-xs">
            {utcDateTime.toFormat("ZZZZ")}
          </div>
        </div>
      );
    },
  },
  {
    id: "download-url",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Price File" />
    ),
    cell: ({
      row: {
        original: { unitPriceFileUrl },
      },
    }) => {
      if (!unitPriceFileUrl) {
        return <></>;
      }
      return (
        <a
          href={unitPriceFileUrl}
          aria-label="download link"
          onClick={(event) => event.stopPropagation()}
        >
          <RiDownload2Fill />
        </a>
      );
    },
  },
];
